import React, { lazy, Suspense, useEffect, useMemo, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';

import cn from 'classnames';
import { useInView } from 'framer-motion';
import Heading from 'components/shared/heading';
import PhoneIcon from 'components/icons/Phone';
import ArrowRightIcon from 'components/icons/ArrowRight';
import styles from './styles.module.scss';
import Button from '../../shared/button';
import useVideo from '../../../hooks/use-video';

const WistiaPlayerLazy = lazy(() => import('../../shared/wistia-player'));

const VerticalMediaWithText = ({
  video = null,
  wistiaVideoId = null,
  image = null,
  title = null,
  titleTag = 'h2',
  subTitle = null,
  text = null,
  theme = null,
  buttonLabel = null,
  buttonLink = null,
  secondaryButtonLink = null,
  secondaryButtonLabel = null,
}) => {
  const containerRef = React.useRef(null);
  const { videoWrapperRef, videoRef, isVideoMuted, handleVideoEnd } = useVideo();
  // Create a ref for the image container
  const mediaContainerRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  const appendImageToCard = useCallback((cardId) => {
    const imageDiv = document.getElementById(`img-${cardId}`);
    if (imageDiv && mediaContainerRef.current) {
      mediaContainerRef.current.appendChild(imageDiv);
    }
  }, []);

  // Handle image appending for this specific card
  useEffect(() => {
    if (image?.id && !video) {
      appendImageToCard(image.id);
    }
  }, [image, video, appendImageToCard]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const isInView = useInView(containerRef);

  // console.log('isInView', isInView);

  const hasMedia = useMemo(
    () => !!(image || video || wistiaVideoId),
    [image, video, wistiaVideoId]
  );
  const hasText = useMemo(() => !!text || !!subTitle, [text, subTitle]);
  const isPhone = useMemo(() => buttonLink?.includes('tel:'), [buttonLink]);
  const isSecondarryButtonPhone = useMemo(
    () => secondaryButtonLink?.includes('tel:'),
    [secondaryButtonLink]
  );

  if (!hasMedia && !hasText) {
    return null;
  }
  return (
    <section
      className={cn(styles.mediaWrapper, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
      ref={containerRef}
    >
      <div className={styles.inner}>
        <div className="container">
          {title && (
            <Heading className={styles.title} tag={titleTag} size="xxl">
              {title}
            </Heading>
          )}
          {subTitle && (
            <div className={styles.subTitle}>
              <p>{subTitle}</p>
            </div>
          )}

          <div
            className={cn(styles.mediaBox, { [styles.fadeIn]: isInView })}
            ref={video ? videoWrapperRef : mediaContainerRef}
          >
            {video && !wistiaVideoId && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                muted={isVideoMuted}
                controls
                playsInline
                onEnded={handleVideoEnd}
                ref={videoRef}
                autoPlay
              >
                <source src={video.url} type="video/mp4" />
              </video>
            )}
            {isClient && wistiaVideoId && (
              <Suspense fallback={null}>
                <WistiaPlayerLazy
                  mediaId={wistiaVideoId}
                  autoPlay
                  playlistLoop
                  muted={isVideoMuted}
                  className={styles.wistiaPlayer}
                  poster={false}
                  swatch={false}
                  resumable
                />
              </Suspense>
            )}
          </div>
          {text && (
            <div className={styles.text}>
              <div dangerouslySetInnerHTML={{ __html: text }} />
            </div>
          )}
          {buttonLabel && buttonLink && (
            <div className={styles.btnBox}>
              <Button
                theme="accent-primary"
                to={buttonLink}
                className={cn(styles.btn, { [styles.btnPhone]: isPhone })}
              >
                {isPhone && <PhoneIcon />}
                {buttonLabel}
                {!isPhone && <ArrowRightIcon />}
              </Button>
            </div>
          )}
          {secondaryButtonLink && secondaryButtonLabel && (
            <div className={styles.secondaryBtnBox}>
              <Link to={secondaryButtonLink} className={styles.secondaryBtn}>
                {isSecondarryButtonPhone && <PhoneIcon />}
                <span>{secondaryButtonLabel}</span>
                {!isSecondarryButtonPhone && <ArrowRightIcon />}
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

VerticalMediaWithText.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.number,
    url: PropTypes.string,
  }),
  wistiaVideoId: PropTypes.string,
  image: PropTypes.shape({
    id: PropTypes.number,
  }),
  title: PropTypes.string,
  titleTag: PropTypes.oneOf(['h1', 'h2', 'h3']),
  subTitle: PropTypes.string,
  text: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  secondaryButtonLink: PropTypes.string,
  secondaryButtonLabel: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
};

export default VerticalMediaWithText;
